import React, { useState } from 'react';
import { BarChart3, History, Settings, User, Target } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { AssessmentHistory } from './AssessmentHistory';
import { TrendAnalysis } from './TrendAnalysis';
import { useSupabaseAuth } from '../../hooks/useSupabase';

type DashboardTab = 'history' | 'trends' | 'settings';

export const UserDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('history');
  const { user } = useSupabaseAuth();

  const tabs = [
    { id: 'history' as const, label: 'Assessment History', icon: History },
    { id: 'trends' as const, label: 'Trend Analysis', icon: BarChart3 },
    { id: 'settings' as const, label: 'Settings', icon: Settings },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'history':
        return <AssessmentHistory />;
      case 'trends':
        return <TrendAnalysis />;
      case 'settings':
        return (
          <Card variant="red">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Dashboard Settings
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Information</h3>
                <div className="bg-red-50 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-8 w-8 text-red-500" />
                    <div>
                      <p className="font-medium text-gray-900">{user?.email}</p>
                      <p className="text-sm text-gray-600">Red Set Security Researcher</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Management</h3>
                <div className="space-y-3">
                  <Button variant="secondary" size="sm">
                    Export All Assessment Data
                  </Button>
                  <Button variant="danger" size="sm">
                    Delete Assessment History
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Preferences</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                    <input type="checkbox" className="rounded border-red-500/30" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Auto-save Assessments</span>
                    <input type="checkbox" defaultChecked className="rounded border-red-500/30" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Advanced Scoring</span>
                    <input type="checkbox" defaultChecked className="rounded border-red-500/30" />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      default:
        return <AssessmentHistory />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Security Assessment Dashboard</h1>
          <p className="text-gray-300">Monitor your AI security testing progress and vulnerability trends</p>
        </div>

        <div className="mb-6">
          <div className="border-b border-red-500/30">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-red-500 text-red-400'
                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card variant="red">
              <div className="flex items-center space-x-3">
                <Target className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">12</p>
                  <p className="text-sm text-gray-600">Total Assessments</p>
                </div>
              </div>
            </Card>
            <Card variant="red">
              <div className="flex items-center space-x-3">
                <BarChart3 className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">67</p>
                  <p className="text-sm text-gray-600">Avg Risk Score</p>
                </div>
              </div>
            </Card>
            <Card variant="red">
              <div className="flex items-center space-x-3">
                <History className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">-15%</p>
                  <p className="text-sm text-gray-600">Risk Improvement</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};